<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="rows"
    :loading="loading"
    row-key="id"
    :row-selection="rowSelection"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Employee'">
        <div class="d-flex align-items-center file-name-icon">
          <DqAvatar
            :src="record?.avatar_url || ''"
            :name="record.employee_name || ''"
            size-class="avatar-md"
            shape-class="rounded-circle"
            class="me-2"
          />
          <div class="ms-1">
            <h6 class="fw-medium mb-0">{{ record.employee_name || "-" }}</h6>
            <span class="fs-12 fw-normal">{{
              record.designation_name || ""
            }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'Leave_Type'">
        <div class="d-flex align-items-center">
          <p class="fs-14 fw-medium mb-0">
            {{ record.leave_type?.name || "-" }}
          </p>
          <a
            v-if="record.reason"
            class="ms-2"
            data-bs-toggle="tooltip"
            :data-bs-title="record.reason"
          >
            <i class="ti ti-info-circle text-info"></i>
          </a>
        </div>
      </template>

      <template v-else-if="column.key === 'From'">
        {{ formatDate(record.from_date) }}
      </template>
      <template v-else-if="column.key === 'To'">
        {{ formatDate(record.to_date) }}
      </template>
      <template v-else-if="column.key === 'No_of_Days'">
        {{ record.days }} {{ record.days === 1 ? "Day" : "Days" }}
      </template>

      <template v-else-if="column.key === 'Status'">
        <span class="btn btn-sm btn-white d-inline-flex align-items-center">
          <span
            class="rounded-circle d-flex justify-content-center align-items-center me-2"
            :class="statusBadgeCls(record.status)[0]"
          >
            <i
              class="ti ti-point-filled"
              :class="statusBadgeCls(record.status)[1]"
            ></i>
          </span>
          {{ humanStatus(record.status) }}
        </span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <a
            v-if="canApproveRecord(record)"
            class="me-2"
            href="javascript:void(0);"
            :title="approveTitle(record)"
            @click="$emit('approve', record.id)"
          >
            <i class="ti ti-check text-success"></i>
          </a>
          <a
            v-if="canDeclineRecord(record)"
            class="me-2"
            href="javascript:void(0);"
            title="Decline"
            @click="$emit('decline', { id: record.id })"
          >
            <i class="ti ti-x text-danger"></i>
          </a>
          <a
            v-if="record.status === 'Approved'"
            href="javascript:void(0);"
            @click="$emit('revoke', record.id)"
            v-can="'Leaves.Approve'"
            title="Revoke approved leave"
          >
            <i class="ti ti-ban text-warning"></i>
          </a>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import moment from "moment";
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  selectedRowKeys: { type: Array, default: () => [] },
  canDecide: { type: Boolean, default: false },
  /** HR/Admin final approver — can act on ManagerApproved */
  canFinalApprove: { type: Boolean, default: false },
});

const emit = defineEmits([
  "approve",
  "decline",
  "revoke",
  "update:selectedRowKeys",
]);

const columns = [
  { title: "Employee", dataIndex: "employee_name", key: "Employee" },
  { title: "Leave Type", dataIndex: "leave_type", key: "Leave_Type" },
  { title: "From", dataIndex: "from_date", key: "From" },
  { title: "To", dataIndex: "to_date", key: "To" },
  { title: "No of Days", dataIndex: "days", key: "No_of_Days" },
  { title: "Status", dataIndex: "status", key: "Status" },
  { title: "", key: "action" },
];

function isActionableStatus(status) {
  if (props.canFinalApprove) {
    return status === "New" || status === "ManagerApproved";
  }
  return status === "New";
}

function canApproveRecord(record) {
  return props.canDecide && isActionableStatus(record.status);
}

function canDeclineRecord(record) {
  return props.canDecide && isActionableStatus(record.status);
}

function approveTitle(record) {
  if (props.canFinalApprove) {
    return record.status === "ManagerApproved" ? "Final approve (HR)" : "Approve";
  }
  return "Recommend to HR";
}

const rowSelection = computed(() => ({
  selectedRowKeys: props.selectedRowKeys,
  onChange: (keys) => emit("update:selectedRowKeys", keys),
  getCheckboxProps: (record) => ({
    disabled: !isActionableStatus(record.status),
  }),
}));

function formatDate(d) {
  return d ? moment(d).format("DD MMM YYYY") : "-";
}

const STATUS_LABELS = {
  New: "Pending Manager",
  ManagerApproved: "Awaiting HR",
  Approved: "Approved",
  Declined: "Declined",
  Cancelled: "Cancelled",
};

function humanStatus(s) {
  return STATUS_LABELS[s] || s || "-";
}

function statusBadgeCls(s) {
  if (s === "Approved") return ["bg-transparent-success", "text-success"];
  if (s === "Declined") return ["bg-transparent-danger", "text-danger"];
  if (s === "Cancelled") return ["bg-transparent-secondary", "text-secondary"];
  if (s === "ManagerApproved") return ["bg-transparent-info", "text-info"];
  if (s === "New") return ["bg-transparent-warning", "text-warning"];
  return ["bg-transparent-secondary", "text-secondary"];
}
</script>
